<template>

    <div id="manage-funds">

        <div class="mb-4">
            <h1 class="text-2xl font-medium text-gray-900 capitalize">Add Payment</h1>
        </div>

        <div class="flex flex-col" v-if="loaded">


           <form action="#" method="POST" @submit.prevent="form.createPayment()">

                <div class="panel">

                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Account</h3>
                            <p>
                                Select the account you want to make the payment against.
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('account_id') }">
                                    <label for="account_id" class="block text-sm font-medium text-gray-700">Account<i></i></label>
                                    <select name="account_id" id="account_id" v-model="form.data.account_id" @change="form.selectAlternateAccount()">
                                        <option :value="null">Select Account</option>
                                        <option :value="account.id" v-for="account in form.accounts">{{ account.name }}</option>
                                    </select>
                                    <span v-if="form.fieldHasErrors('account_id')">{{ form.getFieldError('account_id') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div class="panel" v-show="form.data.account_id">

                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Payment</h3>
                            <p>
                                Make a payment against an accounts fund; adding available funds to spend. Enter the value in the standard format for your currency, e.g. 10 GBP would be 10.00
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('account_fund_id') }">
                                    <label for="payment_account_fund_id" class="block text-sm font-medium text-gray-700">Account Fund<i></i></label>
                                    <select name="account_fund_id" id="payment_account_fund_id" v-model="form.data.account_fund_id" @change="setCurrency">
                                        <option :value="null">Select Account Fund</option>
                                        <option :value="fund.id" v-for="fund in form.account_funds">{{ fund.currency }} {{ fund.type }} - {{ formatCurrency(fund.currency, fund.amount / 100) }}</option>
                                    </select>
                                    <span v-if="form.fieldHasErrors('account_fund_id')">{{ form.getFieldError('account_fund_id') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-2" :class="{ 'form-control-error': form.fieldHasErrors('reference') }">
                                    <label for="reference" class="block text-sm font-medium text-gray-700">Reference<i></i></label>
                                    <input type="text" name="reference" id="reference" v-model="form.data.reference" @change="updatePaymentReference">
                                    <span v-if="form.fieldHasErrors('reference')">{{ form.getFieldError('reference') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('amount') }">
                                    <label for="amount" class="block text-sm font-medium text-gray-700">Amount<i></i></label>
                                    <input type="text" name="amount" id="amount" v-model="form.data.amount">
                                    <span v-if="form.fieldHasErrors('amount')">{{ form.getFieldError('amount') }}</span>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>

                <div class="panel" v-show="form.data.account_id">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Payment Method</h3>
                            <p>
                                Select the method you would like to use for this payment.
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6" v-if="form.selected_currency">

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('gateway') }">
                                    <label for="gateway" class="block text-sm font-medium text-gray-700">Payment Type<i></i></label>
                                    <select name="gateway" id="gateway" v-model="form.data.gateway" @change="selectedGateway">
                                        <option :value="null">Select Payment Type</option>
                                        <option :value="gateway_type.id" v-for="gateway_type in form.gateway_types">{{ gateway_type.name }}</option>
                                    </select>
                                    <span v-if="form.fieldHasErrors('gateway')">{{ form.getFieldError('gateway') }}</span>
                                </div>

                                <div class="mt-6 col-span-6 xl:col-span-4" v-if="form.data.gateway === 'bacs'">

                                    <div class="px-3 py-4">
                                        <h3 class="font-medium">BACS Payment Information</h3>

                                        <p class="py-2">
                                            To make a BACS payment add a payment with the reference <span class="font-medium text-brand-700 underline">{{ form.data.reference }}</span>
                                            to the bank account details below. Your payment will be checked and credited to your account (please note: this can take up to 24 hours to
                                            process once the payment reaches our bank account.)
                                        </p>

                                        <div class="">
                                            <table class="min-w-full divide-y divide-gray-200">
                                                <thead class="bg-gray-50">
                                                <tr>
                                                    <th scope="col" colspan="2" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                                                        {{ $parent.app_name }} Bank Details
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody class="text-gray-600 text-xs">
                                                <tr class="colored-row">
                                                    <td class="p-3">
                                                        Payee Name
                                                    </td>
                                                    <td class="text-center font-medium">
                                                        {{ form.getBankingDetails('payee') }}
                                                    </td>
                                                </tr>
                                                <tr class="colored-row">
                                                    <td class="p-3">
                                                        Sort Code
                                                    </td>
                                                    <td class="text-center font-medium">
                                                        {{ form.getBankingDetails('sort_code') }}
                                                    </td>
                                                </tr>
                                                <tr class="colored-row">
                                                    <td class="p-3">
                                                        Account Number
                                                    </td>
                                                    <td class="text-center font-medium">
                                                        {{ form.getBankingDetails('account_number') }}
                                                    </td>
                                                </tr>
                                                <tr class="colored-row">
                                                    <td class="p-3">
                                                        IBAN
                                                    </td>
                                                    <td class="text-center font-medium">
                                                        {{ form.getBankingDetails('iban') }}
                                                    </td>
                                                </tr>
                                                <tr class="colored-row">
                                                    <td class="p-3">
                                                        Swift Code
                                                    </td>
                                                    <td class="text-center font-medium">
                                                        {{ form.getBankingDetails('swift') }}
                                                    </td>
                                                </tr>
                                                <tr class="colored-row">
                                                    <td class="p-3">
                                                        Reference
                                                    </td>
                                                    <td class="text-center font-medium">
                                                        {{ form.data.reference }}
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                </div>

                            </div>
                            <div class="text-sm text-gray-500 py-6" v-else>
                                Please select an account fund above before selecting your desired payment method.
                            </div>

                        </div>
                    </div>
                </div>

                <div class="flex mt-6 justify-end">

                    <div class="flex border-l-4 border-brand-500 bg-white text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-brand-400" v-if="form.successful()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <p class="text-gray-700 text-sm" v-html="form.getSuccessMessage()"></p>
                    </div>

                    <div class="flex border-l-4 border-error-500 bg-error-50 text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-error-400" v-if="form.hasError()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <p class="text-error-500 text-sm" v-html="form.getErrorMessage()"></p>
                    </div>

                    <button type="submit" class="btn-outline justify-center px-6 ml-6" @click.prevent="goBack">
                        Go Back
                    </button>

                    <button type="submit" class="btn justify-center px-6 ml-6" :disabled="form.isFormUpdating()" v-if="form.data.account_id">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="form.isFormUpdating()">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Add Payment
                    </button>
                </div>
            </form>

        </div>
        <Loading message="Loading Funds" v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {Funds} from "../../app/Funds/Funds";
import {FormattingMixin, NavigationMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "AddPayment",
    mixins: [PermissionMixin, NavigationMixin, FormattingMixin],
    data() {
        return {
            form: null,
            current_user: null,
            loaded: false,
        }
    },
    components: {
        Loading,
    },
    methods: {
        updatePaymentReference() {
            this.$forceUpdate();
        },
        setCurrency() {
            let vm = this;

            try {
                let fund = vm.form.account_funds.find(fund => fund.id === vm.form.data.account_fund_id);

                vm.form.selected_currency = fund.currency;
            } catch (error) {
                vm.form.selected_currency = false;
            }
        },
        selectedGateway() {
            this.$forceUpdate();
        }
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.form = new Funds();

           vm.form.getAccounts().then(response => {
                resolve(true);
            }).catch(error => vm.handleError(error));

        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>


